import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faTimes,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import LogoCircle from "../../assets/image/EdBrowser-logo-circle.png";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(8, 24, 38, 0.55);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.18s ease-out;
`;

const Card = styled.div`
  position: relative;
  width: min(420px, 100%);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(8, 24, 38, 0.35);
  text-align: center;
  font-family: "Source Sans Pro", sans-serif;
  animation: ${popIn} 0.22s ease-out;
`;

const Banner = styled.div`
  height: 84px;
  background: linear-gradient(
    135deg,
    ${({ colors }) => colors.darkBlue},
    ${({ colors }) => colors.myokBlue}
  );
`;

const Badge = styled.div`
  position: relative;
  width: 84px;
  height: 84px;
  margin: -42px auto 0;
  border-radius: 50%;
  background: #fff;
  border: 4px solid #fff;
  box-shadow: 0 6px 18px rgba(8, 24, 38, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
`;

const BadgeIcon = styled.span`
  position: absolute;
  right: -4px;
  bottom: -2px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid #fff;
  background: ${({ $tone }) => $tone};
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  padding: 14px 28px 26px;
`;

const Title = styled.h3`
  margin: 0 0 10px;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${({ colors }) => colors.darkBlue};
`;

const Message = styled.p`
  margin: 0 0 8px;
  font-size: 1rem;
  line-height: 1.5;
  color: #4a5a68;
`;

const Hint = styled.p`
  margin: 14px 0 22px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.92rem;
  line-height: 1.45;
  color: ${({ colors }) => colors.darkBlue};
  background: rgba(17, 78, 117, 0.08);
`;

const PrimaryButton = styled.button.attrs({ type: "button" })`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: ${({ colors }) => colors.myokBlue};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ colors }) => colors.darkBlue};
  }

  &:focus-visible {
    outline: 3px solid rgba(17, 78, 117, 0.35);
    outline-offset: 2px;
  }
`;

const CloseButton = styled.button.attrs({ type: "button" })`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }
`;

const VARIANTS = {
  underage: {
    icon: faUserShield,
    tone: "#E0913A",
    title: "EdBrowser AI isn't available yet",
    message:
      "The EdBrowser AI chatbot is not available for students under the age of 13, and the information entered on this form has not been saved.",
    hint: "Please ask a parent, guardian, or teacher to help you!",
    button: "OK, got it",
  },
  incomplete: {
    icon: faCalendarDays,
    tone: "#2F80B7",
    title: "Date of birth needed",
    message: "Please fill in all fields for your Date of Birth.",
    hint: "Choose your birth month, day, and year, then select Verify Age.",
    button: "OK",
  },
};

/**
 * Branded replacement for the browser alert() used by the chatbot age check.
 *
 * @param {'underage'|'incomplete'|null} variant  Which message to show; null hides it.
 */
const AgeCheckModal = ({ variant, onClose, colors }) => {
  const buttonRef = useRef(null);
  const content = VARIANTS[variant];

  useEffect(() => {
    if (!content) return undefined;
    buttonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [content, onClose]);

  if (!content) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <Card
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="age-check-title"
        aria-describedby="age-check-message"
        onClick={(event) => event.stopPropagation()}
      >
        <Banner colors={colors} />
        <CloseButton onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <Badge>
          <img src={LogoCircle} alt="" />
          <BadgeIcon $tone={content.tone} aria-hidden="true">
            <FontAwesomeIcon icon={content.icon} />
          </BadgeIcon>
        </Badge>
        <Body>
          <Title id="age-check-title" colors={colors}>
            {content.title}
          </Title>
          <Message id="age-check-message">{content.message}</Message>
          <Hint colors={colors}>{content.hint}</Hint>
          <PrimaryButton ref={buttonRef} colors={colors} onClick={onClose}>
            {content.button}
          </PrimaryButton>
        </Body>
      </Card>
    </Overlay>,
    document.body,
  );
};

export default AgeCheckModal;
